import React from 'react';
import { onFCP, onLCP } from 'web-vitals';
import { onTTVC } from '@dropbox/ttvc';
import { useFilePreviewSessionId } from 'js/file_viewer/api/data';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { PREVIEW_TTVC_DURATION_EVENT, } from 'js/file_viewer/core/logging/constants';
import { getLoggingSession, serializeSession, } from 'js/file_viewer/core/logging/session';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
import { usePrevious } from 'js/file_viewer/hooks/usePrevious';
import { PreviewsEventLogger, PreviewsTimingEvents } from 'js/file_viewer/logging/performance';
// We don't want to run into overlogging and issues like cardinality limits with logging too many events,
// so only log set of imporant metrics to AMIS and we can log everything to hive for more analysis
const LOG_TO_AMIS_METRICS = new Set([
    PreviewsTimingEvents.CONTAINER_MOUNT,
    PreviewsTimingEvents.COLLECTION_VIEWER_MOUNT,
    PreviewsTimingEvents.SDK_FILE_VIEWER_CODE_START,
    PreviewsTimingEvents.FVSDK_GET_RIVIERA_DATA_START_START,
    PreviewsTimingEvents.FVSDK_GET_RIVIERA_DATA,
    PreviewsTimingEvents.FVSDK_TITLE_BAR_MOUNT,
    PreviewsTimingEvents.FVSDK_APP_MOUNT,
    PreviewsTimingEvents.FVSDK_MARK_TTVC,
    PreviewsTimingEvents.PREVIEW_COMPONENT_MOUNT,
    PreviewsTimingEvents.PREVIEW_RENDERER_MOUNT,
    PreviewsTimingEvents.FVSDK_MARK_LCP,
    PreviewsTimingEvents.FVSDK_MARK_FCP,
    PreviewsTimingEvents.ASYNC_IMAGE_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_IMAGE_CONTAINER_START,
    PreviewsTimingEvents.FVSDK_IMAGE_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_IMAGE_ON_LOAD,
    PreviewsTimingEvents.ASYNC_VIDEO_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_VIDEO_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_VIDEO_PLAYER_READY,
    PreviewsTimingEvents.FVSDK_VIDEO_MASTER_PLAYLIST_LOADED,
    PreviewsTimingEvents.ASYNC_DOC_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_DOC_CONTAINER_START,
    PreviewsTimingEvents.FVSDK_DOC_CONTAINER_MOUNT,
    PreviewsTimingEvents.FVSDK_FIRST_DOC_PAGE_ON_LOAD,
    PreviewsTimingEvents.FVSDK_DOC_METADATA_LOAD,
]);
const useTimelineLogger = (metricName, eventsToLog) => {
    const filePreviewSessionId = useFilePreviewSessionId();
    const { logger } = useFileViewerContext();
    const hasLogged = React.useRef(false);
    const onLogTimeline = React.useCallback(() => {
        var _a;
        const startEntry = PreviewsEventLogger.getStartEntry();
        const session = getLoggingSession(filePreviewSessionId);
        if (startEntry && !hasLogged.current) {
            const timeLine = PreviewsEventLogger.getEventTimeline();
            Object.entries(timeLine).forEach(([key, value]) => {
                var _a;
                if (eventsToLog.has(key)) {
                    (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                        eventName: metricName,
                        durationInMs: value,
                        tags: { event: key },
                        session: session ? serializeSession(session) : undefined,
                    });
                }
            });
            (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                eventName: metricName,
                durationInMs: startEntry.startTime,
                tags: { event: 'session_start_since_load' },
                session: session ? serializeSession(session) : undefined,
            });
            hasLogged.current = true;
        }
    }, [logger, metricName, filePreviewSessionId, eventsToLog]);
    return { onLogTimeline };
};
export const useTimingLogging = (previewType) => {
    const haveLoggersBeenInitialized = React.useRef(false);
    const filePreviewSessionId = useFilePreviewSessionId();
    const { logger, isLoggedIn } = useFileViewerContext();
    // We log a different timeline for each previewType to reduce cardinality.
    const { onLogTimeline: onLogTTVCTimeline } = useTimelineLogger(`preview_timeline_ttvc_${previewType}`, LOG_TO_AMIS_METRICS);
    const getLogTags = React.useCallback((navigationType, session) => {
        return {
            navigation_type: navigationType,
            file_flip: String(!!(session === null || session === void 0 ? void 0 : session.extra.was_flipped)),
            logged_in_user: String(isLoggedIn),
        };
    }, [isLoggedIn]);
    const _logTTVC = useStabilizedCallback((ttvc) => {
        var _a;
        PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_MARK_TTVC);
        const session = getLoggingSession(filePreviewSessionId);
        if (ttvc.duration) {
            (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                eventName: PREVIEW_TTVC_DURATION_EVENT,
                durationInMs: ttvc.duration,
                tags: getLogTags(ttvc.detail.navigationType === 'script' ? 'ajax' : 'page_load', session),
                shouldLogToHive: true,
                session: session ? serializeSession(session) : undefined,
            });
        }
        onLogTTVCTimeline();
    });
    /**
     * Log time to largest contentful paint.
     * @note These metrics will SKEW HIGH. `metrics_logger` starts watching for LCP much sooner than the FVSDK starts to
     * load. It reports LCP when the user interacts with the page. That means if the user interacts with the page before
     * the FVSDK mounts, the `largest_contentful_paint` event will fire before we register our event listener.
     * As a result, we will not record LCP when the user interacts with the page before FVSDK mount. This might cause
     * a high skew because users who get a very fast LCP before the FVSDK mounts might be more likely to interact with
     * the page before it finishes loading.
     *
     * LCP is also recorded in the web_perf namespace. To get an idea of the size of the skew, compare the average
     * web_perf LCP to the average LCP recorded in the web_previews namespace.
     */
    const _logLCP = useStabilizedCallback((lcp) => {
        var _a;
        PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_MARK_LCP);
        const session = getLoggingSession(filePreviewSessionId);
        if (lcp.value) {
            // LCP is logged on page unmount, so we need to send the actual recorded time to our timeline event.
            PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_MARK_LCP, lcp.value);
            (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                eventName: 'largest_contentful_paint',
                durationInMs: lcp.value,
                tags: getLogTags(lcp.navigationType, session),
                shouldLogToHive: true,
                session: session ? serializeSession(session) : undefined,
            });
        }
    });
    /**
     * Log time to first contentful paint.
     * @note The same warning about a high skew from _logLCP applies to these data.
     */
    const _logFCP = useStabilizedCallback((fcp) => {
        var _a;
        PreviewsEventLogger.mark(PreviewsTimingEvents.FVSDK_MARK_FCP);
        const session = getLoggingSession(filePreviewSessionId);
        if (fcp.value) {
            (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                eventName: 'first_contentful_paint',
                durationInMs: fcp.value,
                tags: getLogTags(fcp.navigationType, session),
                shouldLogToHive: true,
                session: session ? serializeSession(session) : undefined,
            });
        }
    });
    if (!haveLoggersBeenInitialized.current) {
        onFCP(_logFCP);
        onLCP(_logLCP);
        haveLoggersBeenInitialized.current = true;
    }
    React.useEffect(() => {
        const unsubscribeOnTTVC = onTTVC(_logTTVC);
        return () => {
            if (unsubscribeOnTTVC) {
                unsubscribeOnTTVC();
            }
        };
    }, [_logTTVC]);
};
/**
 * Marks the end of a timer in PreviewsTimer. These timers are separate from our checkpoint timers since they are not
 * counted from the start of the session, but from an arbitrary performance.mark.
 */
export const useRecordPreviewsTimerOnMount = (timer, tags, shouldMeasure = true) => {
    const filePreviewSessionId = useFilePreviewSessionId();
    const { logger } = useFileViewerContext();
    const prevShouldMeasure = usePrevious(shouldMeasure);
    React.useEffect(() => {
        var _a;
        const entries = performance.getEntriesByName(timer, 'mark');
        const session = getLoggingSession(filePreviewSessionId);
        if (entries.length > 0 && shouldMeasure && !prevShouldMeasure) {
            performance.clearMarks(timer);
            // the entries are in chronological order, the last entry is the latest
            const mark = entries[entries.length - 1];
            const duration = performance.now() - mark.startTime;
            (_a = logger === null || logger === void 0 ? void 0 : logger.logDuration) === null || _a === void 0 ? void 0 : _a.call(logger, {
                eventName: timer,
                durationInMs: duration,
                session: session ? serializeSession(session) : undefined,
                tags,
            });
        }
    }, [logger, shouldMeasure, prevShouldMeasure, filePreviewSessionId, tags, timer]);
};
//# sourceMappingURL=timing.preenvesnextjs.map
